/**
 * Modal para confirmar la eliminación de subcategorías.
 *
 * Este componente proporciona una interfaz de confirmación para eliminar
 * subcategorías del sistema. Incluye advertencias sobre el impacto de la
 * eliminación y requiere confirmación explícita del usuario.
 *
 * @author Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 * @date [Fecha de creación: 2025-08-06]
 * @updated [Fecha de última modificación: 2025-08-11]
 * @updatedBy Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 */

import { Box, Divider, Icon, Modal, Typography, Card } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { useMaterialUIController } from "context";
import colors from "assets/theme/base/colors";

/**
 * Modal para confirmar la eliminación de subcategorías
 * @param {Object} props - Propiedades del componente
 * @param {boolean} props.open - Estado de apertura del modal
 * @param {Function} props.handleClose - Función para cerrar el modal
 * @param {Function} props.handleDelete - Función para confirmar la eliminación
 * @param {Object} props.subcategoryData - Datos de la subcategoría a eliminar
 * @param {boolean} props.isDisabled - Estado de deshabilitación del formulario
 */
export function SubCategoryDelete({
  open,
  handleClose,
  handleDelete,
  subcategoryData,
  isDisabled = false,
}) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { white, dark } = colors;
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: "400px",
    borderRadius: "20px",
    boxShadow: 24,
    p: 4,
  };

  /**
   * Maneja la confirmación de eliminación
   */
  const handleConfirmDelete = () => {
    if (handleDelete && subcategoryData) {
      handleDelete(subcategoryData.id);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        sx={{
          textAlign: "center",
          "& .MuiDialog-paper": {
            backgroundColor: darkMode ? "#202940" : "#ffffff",
          }
        }}>
        <DialogTitle
          id="alert-dialog-title"
          sx={{ color: darkMode ? white.main : dark.main }}>
          {"¡Importante!"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="alert-dialog-description"
            sx={{ color: darkMode ? white.main : dark.main }}>
            ¿Está seguro de eliminar la subcategoría: <strong>{subcategoryData?.name}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleClose} disabled={isDisabled}>
            Cancelar
          </MDButton>
          <MDButton color="warning" onClick={handleConfirmDelete} disabled={isDisabled}>
            Eliminar
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

SubCategoryDelete.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  subcategoryData: PropTypes.object,
  isDisabled: PropTypes.bool,
};
